'use client';
"use strict";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.classNameForOptions = void 0;
exports.TouchTarget = TouchTarget;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const react_slot_1 = require("@radix-ui/react-slot");
const utils_1 = require("../utils");
const spinner_1 = __importDefault(require("./spinner"));
// Design-system canonical variants:
//   primary  — blue, the workhorse CTA
//   primaryStrong — primary-600 fill, AA-safe white text (public site primary)
//   hollow   — transparent + primary border
//   white    — white card with neutral-100 border, primary text
//   success  — catch-green (reserved for confirm / "logged" moments)
//   danger   — destructive red
//   ghost    — bg-transparent + neutral text
//   dark     — neutral-900 surface with white text
// All other variants below are preserved aliases so the existing API across the
// five consumer apps keeps working. Don't rename, don't drop. New work prefers
// the canonical variants above.
const classNamesForVariant = {
    primary: 'bg-primary text-white hover:bg-primary-600 disabled:bg-primary',
    // primary-600 fill: white text passes AA at any size (4.9:1). The public site's primary.
    primaryStrong: 'bg-primary-600 text-white hover:bg-primary-700 disabled:bg-primary-600',
    hollow: 'bg-transparent text-primary border border-primary hover:bg-primary-50',
    white: 'bg-white text-primary border border-neutral-100 shadow-xs hover:border-primary-200',
    success: 'bg-primary-green text-white hover:brightness-95',
    danger: 'bg-primary-red text-white hover:brightness-95',
    ghost: 'bg-transparent text-neutral-700 hover:bg-neutral-50 hover:text-neutral-900',
    dark: 'bg-neutral-900 text-white hover:bg-neutral-800',
    black: 'bg-neutral-900 text-white hover:bg-neutral-800',
    outline: 'bg-transparent text-neutral-900 border border-neutral-900 hover:bg-neutral-50',
    blue: 'bg-primary text-white hover:bg-primary-600',
    info: 'bg-primary text-white hover:bg-primary-600',
    warning: 'bg-sup-yellow-500 text-white hover:bg-sup-yellow-600',
    green: 'bg-primary-green text-white hover:brightness-95',
    red: 'bg-primary-red text-white hover:brightness-95',
    destructive: 'bg-primary-red text-white hover:brightness-95',
    facebook: 'bg-facebook text-white shadow-xs',
    'hollow-blue': 'bg-transparent text-primary border border-primary hover:bg-primary-50',
    successLight: 'bg-sup-green-50 text-sup-green-900 border border-sup-green-100 hover:bg-sup-green-100',
    successOutline: 'bg-transparent text-sup-green-700 border border-sup-green-700 hover:bg-sup-green-50',
    neutral: 'bg-neutral-50 text-neutral-700 border border-neutral-100 hover:bg-neutral-100',
    neutralOutline: 'bg-transparent text-neutral-700 border border-neutral-200 hover:bg-neutral-50',
    'neutral-dark': 'bg-neutral-900 text-white hover:bg-neutral-800',
    transparent: 'bg-transparent text-neutral-700 hover:bg-neutral-50',
    text: 'bg-transparent text-primary hover:text-primary-600 px-0!',
    icon: 'bg-transparent text-inherit hover:bg-neutral-50',
    'text-neutral': 'bg-transparent text-neutral-700 hover:text-neutral-900 px-0!',
    'neutral-400': 'bg-neutral-50 text-neutral-700 border border-neutral-100 hover:bg-neutral-100',
    'neutral-500': 'bg-neutral-50 text-neutral-700 border border-neutral-100 hover:bg-neutral-100'
};
// Sizes match the design system spec: md = h-[38px] px-[18px] text-sm rounded-lg.
// `text` and `text-neutral` are link-style and only adjust font size.
const classNamesForSize = {
    xs: (v) => ((v === null || v === void 0 ? void 0 : v.includes('text')) ? 'text-xs' : 'h-7 px-3 text-xs rounded-md'),
    sm: (v) => ((v === null || v === void 0 ? void 0 : v.includes('text')) ? 'text-xs' : 'h-[30px] px-3 text-xs rounded-md'),
    md: (v) => ((v === null || v === void 0 ? void 0 : v.includes('text')) ? 'text-sm' : 'h-[38px] px-[18px] text-sm rounded-lg'),
    lg: (v) => ((v === null || v === void 0 ? void 0 : v.includes('text')) ? 'text-base' : 'h-12 px-[26px] text-base rounded-lg'),
    xl: (v) => ((v === null || v === void 0 ? void 0 : v.includes('text')) ? 'text-lg' : 'h-14 px-7 text-lg rounded-lg')
};
// `disabled` stays in the accepted shape — five apps pass it — but the styling
// comes from the `disabled:` variants, so nothing here reads it.
const classNameForOptions = ({ variant, size, className }) => (0, utils_1.cn)('inline-flex items-center justify-center gap-2 whitespace-nowrap font-bold leading-none cursor-pointer', 'border border-transparent transition-colors duration-150', 'focus-visible:outline-none focus-visible:shadow-[var(--aiq-ring-focus)]', 'disabled:cursor-not-allowed disabled:opacity-45', variant ? classNamesForVariant[variant] : '', size ? classNamesForSize[size](variant) : '', className);
exports.classNameForOptions = classNameForOptions;
exports.default = (0, react_1.forwardRef)(function Button(props, ref) {
    const { className, children, asChild, variant = 'white', size = 'md', loading = false, icon, trailingIcon } = props, rest = __rest(props, ["className", "children", "asChild", "variant", "size", "loading", "icon", "trailingIcon"]);
    const _className = (0, exports.classNameForOptions)({ variant, size, className, disabled: loading || rest.disabled });
    const Comp = asChild ? react_slot_1.Slot : 'button';
    // The icons and the Slottable are direct children of Comp, never wrapped in a Fragment: with asChild, Radix Slot
    // looks for the Slottable among its own children, and inside a Fragment it found none and merged every class onto
    // the Fragment — each <Button asChild> rendered as a bare, unstyled link (QA 2026-10-21 F23).
    if (loading) {
        return ((0, jsx_runtime_1.jsx)(Comp, Object.assign({ className: _className, disabled: true, ref: ref }, rest, { children: (0, jsx_runtime_1.jsx)(spinner_1.default, { className: 'h-4 w-4' }) })));
    }
    return ((0, jsx_runtime_1.jsxs)(Comp, Object.assign({ className: _className, disabled: rest.disabled, ref: ref }, rest, { children: [icon ? (0, jsx_runtime_1.jsx)("span", { "aria-hidden": 'true', className: 'inline-flex shrink-0', children: icon }) : null, (0, jsx_runtime_1.jsx)(react_slot_1.Slottable, { children: children }), trailingIcon ? (0, jsx_runtime_1.jsx)("span", { "aria-hidden": 'true', className: 'inline-flex shrink-0', children: trailingIcon }) : null] })));
});
/**
 * Expand the hit area to at least 44×44px on touch devices.
 */
function TouchTarget({ children }) {
    return ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)("span", { "aria-hidden": 'true', className: 'absolute top-1/2 left-1/2 size-[max(100%,2.75rem)] -translate-x-1/2 -translate-y-1/2 [@media(pointer:fine)]:hidden' }), children] }));
}
